import React, { useContext, useState, useEffect } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import AccountTreeIcon from "@material-ui/icons/AccountTree";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import Chart from "./Chart";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	fixedHeightPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 260,
	},
	customFixedHeightPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 110,
	},
	cardHeader: {
		display: "flex",
		alignItems: "center",
		marginBottom: theme.spacing(1),
	},
	headerIcon: {
		marginRight: theme.spacing(1),
	},
	tablePaper: {
		padding: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		height: 270,
		overflow: "hidden",
	},
	tableContainer: {
		flex: 1,
		maxHeight: 195,
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	userAvatar: {
		width: 26,
		height: 26,
		fontSize: 12,
		marginRight: theme.spacing(1),
		backgroundColor: theme.palette.primary.main,
	},
	colorDot: {
		width: 12,
		height: 12,
		borderRadius: "50%",
		display: "inline-block",
		marginRight: theme.spacing(1),
		flexShrink: 0,
	},
	chipOpen: {
		backgroundColor: "#1976d2",
		color: "#fff",
		fontWeight: 600,
		height: 22,
		minWidth: 26,
		fontSize: "0.75rem",
	},
	chipPending: {
		backgroundColor: "#ed6c02",
		color: "#fff",
		fontWeight: 600,
		height: 22,
		minWidth: 26,
		fontSize: "0.75rem",
	},
	chipClosedToday: {
		backgroundColor: "#2e7d32",
		color: "#fff",
		fontWeight: 600,
		height: 22,
		minWidth: 26,
		fontSize: "0.75rem",
	},
	chipZero: {
		backgroundColor: "transparent",
		color: theme.palette.text.secondary,
		height: 22,
		minWidth: 26,
		fontSize: "0.75rem",
	},
}));

const Dashboard = () => {
	const classes = useStyles();

	const { user } = useContext(AuthContext);
	var userQueueIds = [];

	if (user && user.queues && (user?.queues || []).length > 0) {
		userQueueIds = (user?.queues || []).map(q => q.id);
	}

	const [metrics, setMetrics] = useState({
		totalTickets: 0,
		totalOpen: 0,
		totalPending: 0,
		totalClosed: 0,
		users: [],
		queues: []
	});

	useEffect(() => {
		let isMounted = true;
		const fetchMetrics = async () => {
			try {
				const { data } = await api.get("/dashboard/metrics", {
					params: {
						queueIds: JSON.stringify(userQueueIds)
					}
				});
				if (isMounted) {
					setMetrics(data);
				}
			} catch (err) {
				console.error(err);
			}
		};

		fetchMetrics();
		const interval = setInterval(fetchMetrics, 15000);
		return () => {
			isMounted = false;
			clearInterval(interval);
		};
	}, [userQueueIds.length]);

	return (
		<div>
			<Container maxWidth="lg" className={classes.container}>
				<Grid container spacing={3}>
					{/* Fila 1: Estado global de tickets */}
					<Grid item xs={12} sm={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden", borderLeft: "5px solid #1976d2" }}>
							<Typography component="h3" variant="subtitle1" color="primary" style={{ fontWeight: 600 }}>
								{i18n.t("dashboard.messages.inAttendance.title")}
							</Typography>
							<Grid item style={{ marginTop: 8 }}>
								<Typography component="h1" variant="h4" color="textPrimary">
									{metrics.totalOpen || 0}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden", borderLeft: "5px solid #ed6c02" }}>
							<Typography component="h3" variant="subtitle1" style={{ color: "#ed6c02", fontWeight: 600 }}>
								{i18n.t("dashboard.messages.waiting.title")}
							</Typography>
							<Grid item style={{ marginTop: 8 }}>
								<Typography component="h1" variant="h4" color="textPrimary">
									{metrics.totalPending || 0}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden", borderLeft: "5px solid #2e7d32" }}>
							<Typography component="h3" variant="subtitle1" style={{ color: "#2e7d32", fontWeight: 600 }}>
								{i18n.t("dashboard.messages.closed.title")}
							</Typography>
							<Grid item style={{ marginTop: 8 }}>
								<Typography component="h1" variant="h4" color="textPrimary">
									{metrics.totalClosed || 0}
								</Typography>
							</Grid>
						</Paper>
					</Grid>

					{/* Fila 2: Repartiment per Empleat i Departament */}
					<Grid item xs={12} md={6}>
						<Paper className={classes.tablePaper}>
							<div className={classes.cardHeader}>
								<PeopleAltIcon color="primary" className={classes.headerIcon} />
								<Typography component="h3" variant="h6" color="primary">
									{i18n.t("dashboard.messages.byUser.title")}
								</Typography>
							</div>
							<TableContainer className={classes.tableContainer}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.userColumn")}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.openColumn")}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.closedTodayColumn")}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.totalColumn")}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{metrics.users && metrics.users.length > 0 ? (
											metrics.users.map(u => (
												<TableRow key={u.id} hover>
													<TableCell style={{ display: "flex", alignItems: "center", borderBottom: "none", padding: "6px 12px" }}>
														<Avatar className={classes.userAvatar}>
															{u.name ? u.name.charAt(0).toUpperCase() : "U"}
														</Avatar>
														<Typography variant="body2" noWrap style={{ maxWidth: 140 }}>
															{u.name}
														</Typography>
													</TableCell>
													<TableCell align="center" style={{ padding: "6px 12px" }}>
														<Chip
															size="small"
															label={u.open}
															className={u.open > 0 ? classes.chipOpen : classes.chipZero}
														/>
													</TableCell>
													<TableCell align="center" style={{ padding: "6px 12px" }}>
														<Chip
															size="small"
															label={u.closedToday}
															className={u.closedToday > 0 ? classes.chipClosedToday : classes.chipZero}
														/>
													</TableCell>
													<TableCell align="center" style={{ fontWeight: 600, padding: "6px 12px" }}>
														{u.total}
													</TableCell>
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell colSpan={4} align="center">
													<Typography variant="body2" color="textSecondary">
														{i18n.t("dashboard.messages.noUsers")}
													</Typography>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					</Grid>

					<Grid item xs={12} md={6}>
						<Paper className={classes.tablePaper}>
							<div className={classes.cardHeader}>
								<AccountTreeIcon style={{ color: "#2e7d32" }} className={classes.headerIcon} />
								<Typography component="h3" variant="h6" style={{ color: "#2e7d32" }}>
									{i18n.t("dashboard.messages.byQueue.title")}
								</Typography>
							</div>
							<TableContainer className={classes.tableContainer}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.queueColumn")}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.pendingColumn")}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.openColumn")}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.closedTodayColumn")}</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("dashboard.messages.totalColumn")}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{metrics.queues && metrics.queues.length > 0 ? (
											metrics.queues.map(q => (
												<TableRow key={q.id} hover>
													<TableCell style={{ display: "flex", alignItems: "center", borderBottom: "none", padding: "6px 12px" }}>
														<span
															className={classes.colorDot}
															style={{ backgroundColor: q.color || "#757575" }}
														/>
														<Typography variant="body2" noWrap style={{ maxWidth: 130 }}>
															{q.name}
														</Typography>
													</TableCell>
													<TableCell align="center" style={{ padding: "6px 12px" }}>
														<Chip
															size="small"
															label={q.pending}
															className={q.pending > 0 ? classes.chipPending : classes.chipZero}
														/>
													</TableCell>
													<TableCell align="center" style={{ padding: "6px 12px" }}>
														<Chip
															size="small"
															label={q.open}
															className={q.open > 0 ? classes.chipOpen : classes.chipZero}
														/>
													</TableCell>
													<TableCell align="center" style={{ padding: "6px 12px" }}>
														<Chip
															size="small"
															label={q.closedToday}
															className={q.closedToday > 0 ? classes.chipClosedToday : classes.chipZero}
														/>
													</TableCell>
													<TableCell align="center" style={{ fontWeight: 600, padding: "6px 12px" }}>
														{q.total}
													</TableCell>
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell colSpan={5} align="center">
													<Typography variant="body2" color="textSecondary">
														{i18n.t("dashboard.messages.noQueues")}
													</Typography>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					</Grid>

					{/* Fila 3: Grafico de tickets */}
					<Grid item xs={12}>
						<Paper className={classes.fixedHeightPaper}>
							<Chart />
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	);
};

export default Dashboard;
